import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Sticky sidebar table-of-contents for long settings pages. Highlights the
 * section currently in view via IntersectionObserver and jumps on click.
 * Handles hash-based deep-linking so /admin/portal-settings#display-agreement
 * opens directly to that section.
 *
 * Nested sub-sections (used for Display Settings' per-page categories) show
 * indented under their parent when the parent is active or a sibling sub is
 * active. This keeps the flat surface clean but lets sub-navigation
 * surface when needed.
 *
 * Props:
 *   sections — [{ id, label, subsections?: [{ id, label }] }]. Each id must
 *     match a DOM element's id in the main content column.
 */
export default function SettingsSidebar({ sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || '');
  const observerRef = useRef(null);

  // Flat list of all watchable ids (parents + subs) so IntersectionObserver
  // sees everything without re-registering as sections expand.
  const allIds = useMemo(() => {
    const ids = [];
    for (const s of sections) {
      ids.push(s.id);
      for (const sub of s.subsections || []) ids.push(sub.id);
    }
    return ids;
  }, [sections]);

  // Track which sections are currently intersecting; pick the topmost one as
  // "active". Using a rootMargin that pushes the trigger line down 30% of the
  // viewport avoids the "everything is active at once" flicker when scrolling
  // between short sections.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.boundingClientRect.top);
          else visible.delete(entry.target.id);
        }
        if (visible.size > 0) {
          // Section whose top is closest to (but not below) the trigger line.
          const [topmostId] = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
          setActiveId(topmostId);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 1] }
    );
    observerRef.current = observer;
    for (const id of allIds) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [allIds]);

  // Deep-link on mount: honour location.hash by scrolling to it after render.
  // useEffect defers until the DOM is committed so scrollIntoView finds the
  // target. A short timeout lets any tall sections above finish laying out.
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    history.replaceState(null, '', `#${id}`);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  const activeParentId = (() => {
    for (const s of sections) {
      if (s.id === activeId) return s.id;
      if ((s.subsections || []).some(sub => sub.id === activeId)) return s.id;
    }
    return null;
  })();

  return (
    <>
      {/* Mobile top nav — horizontal select. Hidden ≥md where the sidebar shows. */}
      <div className="md:hidden mb-4">
        <select
          value={activeId}
          onChange={(e) => scrollTo(e.target.value)({ preventDefault: () => {} })}
          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 dark:text-white"
        >
          {sections.map(s => (
            <optgroup key={s.id} label={s.label}>
              <option value={s.id}>{s.label}</option>
              {(s.subsections || []).map(sub => (
                <option key={sub.id} value={sub.id}>{'  '}{sub.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Desktop sidebar. Sticky so it stays visible as you scroll the main
          column. The offset (top-20 / 5rem) clears the DashboardLayout's
          sticky header (h-16 + border ≈ 65px), otherwise the top of the TOC
          would slide under the header and vanish. max-h keeps the sidebar
          scrollable internally when it grows past the visible viewport. */}
      <nav className="hidden md:block sticky top-20 self-start w-56 shrink-0 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1">
        <ul className="space-y-0.5 text-sm">
          {sections.map(s => {
            const isActive = activeParentId === s.id;
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  onClick={scrollTo(s.id)}
                  className={`block px-3 py-1.5 rounded transition-colors ${
                    activeId === s.id
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 font-medium'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                  }`}
                >
                  {s.label}
                </a>
                {isActive && (s.subsections || []).length > 0 && (
                  <ul className="mt-0.5 mb-1 ml-3 border-l border-neutral-200 dark:border-neutral-700 space-y-0.5">
                    {s.subsections.map(sub => (
                      <li key={sub.id}>
                        <a
                          href={`#${sub.id}`}
                          onClick={scrollTo(sub.id)}
                          className={`block px-3 py-1 rounded text-xs transition-colors ${
                            activeId === sub.id
                              ? 'text-primary-700 dark:text-primary-300 font-medium'
                              : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                          }`}
                        >
                          {sub.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
